import type { Ref } from 'react'
import React, { forwardRef, useMemo } from 'react'
import { Field } from 'react-final-form'
import { useIntl } from 'react-intl'
import type { FieldValidator } from 'final-form'
import {
  digitsFormatter,
  moneyFloatFormatter,
  moneyFloatParser,
  moneyFormatter,
  numberParser,
  numberParserWithFloat,
} from '@/components/ui/fields/TextInput/helpers'
import { normalizePhoneNumber } from '@/utils/formatUtils'
import {
  getValidateFunction,
  hasMaxValue,
  hasMinValue,
  isFieldRequired,
  isMaxLength,
  isMinLength,
  isPhoneNumber,
  isValidEmail,
  numbersAfterFloatValid,
} from '@/utils/validators'
import type { ITextInputProps, TTextInputFormatter, TTextInputViewProps } from './interfaces'
import TextInputView from './TextInputView'

const TextInput = forwardRef((props: ITextInputProps, ref: Ref<HTMLInputElement>) => {
  const {
    isRequired,
    isRequiredWithoutSign,
    type = 'text',
    minValue,
    maxValue,
    customValidator,
    minLength,
    maxLength,
    minValueErrorMessageId,
    maxValueErrorMessageId,
    isHidden,
    numAfterDot,
    numbersAfterDotErrorMessageId,
  } = props

  const intl = useIntl()

  const parse = useMemo(() => {
    if (props.parse) return props.parse

    switch (type) {
      case 'price':
      case 'num':
        return numberParser
      case 'numberFloat':
        return numberParserWithFloat
      case 'floatPrice':
        return moneyFloatParser(numAfterDot)
      case 'tel':
        return normalizePhoneNumber
      case 'digits':
        return digitsFormatter
      default:
        return undefined
    }
  }, [type, props.parse, numAfterDot])

  const format: TTextInputFormatter | undefined = useMemo(() => {
    if (props.format) return props.format

    switch (type) {
      case 'price':
        return value => moneyFormatter(value, intl)
      case 'floatPrice':
        return value => moneyFloatFormatter(numAfterDot)(value, intl)
      default:
        return undefined
    }
  }, [type, props.format, numAfterDot, intl])

  if (isHidden) return null

  const validate = getValidateFunction(() => {
    const validators: FieldValidator<string>[] = []
    if (isRequired || isRequiredWithoutSign) validators.push(isFieldRequired)
    if (type === 'tel') validators.push(isPhoneNumber)
    if (type === 'email') validators.push(isValidEmail)

    if (type === 'num' || type === 'numberFloat' || type === 'price') {
      if (minValue || minValue === 0) validators.push(hasMinValue(minValue, minValueErrorMessageId))
      if (maxValue) validators.push(hasMaxValue(maxValue, maxValueErrorMessageId))
    }

    if (type === 'numberFloat' && numAfterDot) {
      validators.push(numbersAfterFloatValid(numAfterDot, numbersAfterDotErrorMessageId))
    }

    if (customValidator) validators.push(customValidator)
    if (minLength) validators.push(isMinLength(minLength))
    if (maxLength) validators.push(isMaxLength(maxLength))

    return validators
  }) as FieldValidator<string>

  return (
    <Field<string> validate={validate} {...props} parse={parse} format={format} ref={ref}>
      {(fieldProps: TTextInputViewProps) => <TextInputView {...fieldProps} {...props} />}
    </Field>
  )
})

export default TextInput
